import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderOpen, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { describeChange, type Translate } from '@/lib/watchText';
import { workspaceHref } from '@/lib/workspaceHref';
import { AlertsInbox, CalendarCard, SectorsCard } from '@/pages/workspace/AlertsInbox';
import {
  createFolder,
  deleteFolder,
  deleteWorkspaceItem,
  fetchFolders,
  fetchWorkspace,
  fetchWorkspaceChanges,
  markWorkspaceSeen,
  moveWorkspaceItem,
  renameFolder,
  saveWorkspaceItem,
  type ClientFolder,
  type SharedWorkspaceItem,
  type WatchPauta,
  type WorkspaceItem,
  type WorkspaceWatch,
} from '@/services/workspace';

function PautaLine({ pauta }: { pauta: WatchPauta }) {
  const { t, i18n } = useTranslation(['common', 'cade']);
  const kind =
    pauta.session_kind === 'EXTRAORDINARY'
      ? t('cade:pautas.extraordinary')
      : t('cade:pautas.ordinary');
  const session =
    pauta.session_number !== null
      ? t('cade:pautas.session', { number: pauta.session_number, kind })
      : t('cade:pautas.sessionNoNumber', { kind });
  const date = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${pauta.session_date}T00:00:00Z`));
  return (
    <p
      className="rounded-md border border-primary/40 bg-primary/5 p-2 text-xs text-foreground"
      data-watch-pauta={pauta.standing}
    >
      {t('common:workspace.onPauta', {
        session,
        date,
        standing: t(`cade:pautas.standing.${pauta.standing}`),
      })}
    </p>
  );
}

function WatchLine({
  watch,
  item,
  onChanged,
}: {
  watch: WorkspaceWatch | undefined;
  item: WorkspaceItem;
  onChanged: () => void;
}) {
  const { t } = useTranslation(['common', 'cade']);
  const seen = useMutation({
    mutationFn: () => markWorkspaceSeen(item.id),
    onSuccess: onChanged,
  });
  if (!watch) return null;
  const pauta = watch.pauta ? <PautaLine pauta={watch.pauta} /> : null;
  if (watch.status === 'unavailable') {
    return (
      <>
        {pauta}
        <p className="text-xs text-muted-foreground" data-watch="unavailable">
          {t('common:workspace.watch.unavailable')}
        </p>
      </>
    );
  }
  if (watch.status === 'unchanged') {
    return (
      <>
        {pauta}
        <p className="text-xs text-muted-foreground" data-watch="unchanged">
          {t(`common:workspace.watch.unchanged_${item.kind}`)}
        </p>
      </>
    );
  }
  return (
    <>
      {pauta}
      <div
        className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-foreground"
        data-watch="changed"
      >
        <p className="font-medium">{t('common:workspace.watch.changed')}</p>
        <ul className="list-disc pl-4">
          {watch.changes.map((change) => (
            <li key={change.field}>{describeChange(change, t as unknown as Translate)}</li>
          ))}
        </ul>
        <button
          type="button"
          disabled={seen.isPending}
          onClick={() => seen.mutate()}
          className="rounded-md border px-2 py-1 text-xs text-foreground disabled:opacity-50"
        >
          {t('common:workspace.watch.markSeen')}
        </button>
      </div>
    </>
  );
}

/** Where one of the caller's items is shared: private, or one of the firm's client folders. */
function FolderSelect({
  item,
  folders,
  onChanged,
}: {
  item: WorkspaceItem;
  folders: ClientFolder[];
  onChanged: () => void;
}) {
  const { t } = useTranslation('common');
  const move = useMutation({
    mutationFn: (folderId: string | null) => moveWorkspaceItem(item.id, folderId),
    onSuccess: onChanged,
  });
  return (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <FolderOpen className="h-3.5 w-3.5" aria-hidden />
      {t('workspace.folders.shareIn')}
      <select
        value={item.folder_id ?? ''}
        disabled={move.isPending}
        onChange={(event) => move.mutate(event.target.value || null)}
        className="rounded-md border bg-background px-2 py-1 text-xs text-foreground"
        data-item-folder
      >
        <option value="">{t('workspace.folders.private')}</option>
        {folders.map((folder) => (
          <option key={folder.id} value={folder.id}>
            {folder.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ItemRow({
  item,
  watch,
  onChanged,
  folders,
}: {
  item: WorkspaceItem;
  watch: WorkspaceWatch | undefined;
  onChanged: () => void;
  folders: ClientFolder[];
}) {
  const { t, i18n } = useTranslation('common');
  const [note, setNote] = useState(item.note);
  const save = useMutation({
    mutationFn: () =>
      saveWorkspaceItem({
        kind: item.kind,
        agency: item.agency,
        key: item.key,
        label: item.label,
        note,
      }),
    onSuccess: onChanged,
  });
  const remove = useMutation({
    mutationFn: () => deleteWorkspaceItem(item.id),
    onSuccess: onChanged,
  });
  const href = workspaceHref(item);

  return (
    <li className="space-y-2 rounded-md border p-3" data-workspace-item={item.kind}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="space-x-2">
          {href ? (
            <Link to={href} className="font-medium text-primary underline-offset-4 hover:underline">
              {item.label || item.key}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{item.label || item.key}</span>
          )}
          <span className="text-xs uppercase text-muted-foreground">{item.agency}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {t('workspace.updated', {
            date: new Date(item.updated_at).toLocaleDateString(i18n.language),
          })}
        </span>
      </div>
      <WatchLine watch={watch} item={item} onChanged={onChanged} />
      {folders.length > 0 ? (
        <FolderSelect item={item} folders={folders} onChanged={onChanged} />
      ) : null}
      <textarea
        value={note}
        maxLength={5000}
        rows={2}
        aria-label={t('workspace.noteLabel')}
        onChange={(event) => setNote(event.target.value)}
        className="w-full rounded-md border bg-background p-2 text-sm text-foreground"
      />
      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={save.isPending || note === item.note}
          onClick={() => save.mutate()}
          className="rounded-md border px-3 py-1 text-sm text-foreground disabled:opacity-50"
        >
          {t('workspace.saveNote')}
        </button>
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          {t('workspace.remove')}
        </button>
      </div>
    </li>
  );
}

/** One item a colleague shared: read-only, with its owner and note. */
function SharedRow({ item }: { item: SharedWorkspaceItem }) {
  const { t } = useTranslation('common');
  const href = workspaceHref(item);
  return (
    <li className="space-y-1 rounded-md border p-3" data-shared-item={item.kind}>
      <div className="flex flex-wrap items-baseline gap-2">
        {href ? (
          <Link to={href} className="font-medium text-primary underline-offset-4 hover:underline">
            {item.label || item.key}
          </Link>
        ) : (
          <span className="font-medium text-foreground">{item.label || item.key}</span>
        )}
        <span className="text-xs uppercase text-muted-foreground">{item.agency}</span>
        <span className="text-xs text-muted-foreground">
          {t('workspace.folders.sharedBy', { name: item.owner_name })}
        </span>
      </div>
      {item.note ? (
        <p className="whitespace-pre-line text-sm text-foreground">{item.note}</p>
      ) : null}
    </li>
  );
}

/**
 * The firm's client folders: create, rename and delete them, and read what each holds. An item
 * is shared by choosing a folder on it; what a colleague shared is listed here, read-only.
 */
function FoldersSection({
  firm,
  folders,
  mine,
  shared,
  onChanged,
}: {
  firm: string;
  folders: ClientFolder[];
  mine: WorkspaceItem[];
  shared: SharedWorkspaceItem[];
  onChanged: () => void;
}) {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => createFolder(name.trim()),
    onSuccess: () => {
      setName('');
      onChanged();
    },
  });
  const rename = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => renameFolder(id, value),
    onSuccess: onChanged,
  });
  const remove = useMutation({ mutationFn: deleteFolder, onSuccess: onChanged });
  const error = create.error ?? rename.error ?? remove.error;

  return (
    <section className="space-y-3" data-folders>
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-foreground">
          {t('workspace.folders.title', { firm })}
        </h2>
        <p className="text-xs text-muted-foreground">{t('workspace.folders.help')}</p>
      </div>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <label className="space-y-1 text-xs text-muted-foreground">
          {t('workspace.folders.newName')}
          <input
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            className="block w-64 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
            data-new-folder
          />
        </label>
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded-md border px-3 py-1 text-sm text-foreground disabled:opacity-50"
        >
          {t('workspace.folders.create')}
        </button>
      </form>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error.message}
        </p>
      ) : null}
      {folders.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('workspace.folders.none')}</p>
      ) : null}
      {folders.map((folder) => {
        const own = mine.filter((item) => item.folder_id === folder.id);
        const theirs = shared.filter((item) => item.folder_id === folder.id);
        return (
          <div key={folder.id} className="space-y-2 rounded-md border p-3" data-folder={folder.name}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-medium text-foreground">{folder.name}</h3>
              <div className="flex items-center gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    const value = window.prompt(t('workspace.folders.renamePrompt'), folder.name);
                    if (value && value.trim() && value.trim() !== folder.name) {
                      rename.mutate({ id: folder.id, value: value.trim() });
                    }
                  }}
                  className="text-muted-foreground underline-offset-4 hover:underline"
                >
                  {t('workspace.folders.rename')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(t('workspace.folders.deleteConfirm', { name: folder.name }))) {
                      remove.mutate(folder.id);
                    }
                  }}
                  className="text-muted-foreground underline-offset-4 hover:underline"
                >
                  {t('workspace.folders.delete')}
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspace.folders.counts', { mine: own.length, theirs: theirs.length })}
            </p>
            {theirs.length > 0 ? (
              <ul className="space-y-2">
                {theirs.map((item) => (
                  <SharedRow key={item.id} item={item} />
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

function Summaries({ changed, onPauta }: { changed: number; onPauta: number }) {
  const { t } = useTranslation(['common', 'cade']);
  return (
    <>
      {changed > 0 ? (
        <p className="text-sm font-medium text-foreground" role="status" data-watch-summary>
          {t('workspace.watch.summary', { count: changed })}
        </p>
      ) : null}
      {onPauta > 0 ? (
        <p className="text-sm font-medium text-foreground" role="status" data-pauta-summary>
          {t('workspace.pautaSummary', { count: onPauta })}
        </p>
      ) : null}
    </>
  );
}

/**
 * The signed-in user's workspace: marked cases and saved searches, with their notes. Private
 * unless the owner puts an item in one of the firm's client folders, where the firm's other
 * members can read it (and nobody but the owner can change it).
 */
export function WorkspacePage() {
  const { t } = useTranslation(['common', 'cade']);
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['workspace', 'all'],
    queryFn: () => fetchWorkspace(),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['workspace'] });
  const { data: folderData } = useQuery({
    queryKey: ['workspace', 'folders'],
    queryFn: () => fetchFolders(),
  });
  const folders = folderData?.folders ?? [];
  // A separate query so a slow or failing corpus read never blocks the list.
  const { data: changes } = useQuery({
    queryKey: ['workspace', 'changes'],
    queryFn: fetchWorkspaceChanges,
  });

  if (isPending) {
    return (
      <div className="p-6">
        <SkeletonLoader rows={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const changedCount = Object.values(changes?.watch ?? {}).filter(
    (w) => w.status === 'changed',
  ).length;
  const onPautaCount = Object.values(changes?.watch ?? {}).filter((w) => w.pauta).length;
  const cases = data.items.filter((item) => item.kind === 'case');
  const searches = data.items.filter((item) => item.kind === 'search');

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('workspace.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {folderData?.firm ? t('workspace.privateUnlessShared') : t('workspace.private')}
        </p>
      </header>

      <Summaries changed={changedCount} onPauta={onPautaCount} />

      <AlertsInbox />
      <CalendarCard />
      <SectorsCard />

      {folderData?.firm ? (
        <FoldersSection
          firm={folderData.firm}
          folders={folders}
          mine={data.items}
          shared={data.shared ?? []}
          onChanged={refresh}
        />
      ) : null}

      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t('workspace.empty')}
        </p>
      ) : null}

      {cases.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">{t('workspace.cases')}</h2>
          <ul className="space-y-2">
            {cases.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                watch={changes?.watch[item.id]}
                onChanged={refresh}
                folders={folders}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {searches.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground">{t('workspace.searches')}</h2>
          <ul className="space-y-2">
            {searches.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                watch={changes?.watch[item.id]}
                onChanged={refresh}
                folders={folders}
              />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
